/**
 * The per-device data key (ADR-0021).
 *
 * An AES-GCM-256 key generated with WebCrypto as NON-EXTRACTABLE: script on
 * this origin can use it to encrypt/decrypt, but its bytes can never be read,
 * exported or copied off the device. It is persisted as a CryptoKey object
 * (structured clone) in its own small IndexedDB database next to the data
 * database, so it can be destroyed independently: deleting the key makes any
 * leftover ciphertext unreadable (crypto-shredding).
 *
 * Fails closed: without WebCrypto (very old browsers, or a page not served
 * over HTTPS, where browsers hide `crypto.subtle`) nothing is stored.
 */
import Dexie, { type Table } from 'dexie';

export class EncryptionUnavailableError extends Error {
  readonly code = 'ENCRYPTION_UNAVAILABLE';
  constructor() {
    super('This browser cannot encrypt data on the device (WebCrypto is unavailable or the page is not served over HTTPS). Use an up-to-date browser over HTTPS.');
    this.name = 'EncryptionUnavailableError';
  }
}

/** Throws EncryptionUnavailableError unless AES-GCM via WebCrypto is available. */
export function assertWebCrypto(): void {
  const c = globalThis.crypto;
  if (!c || typeof c.getRandomValues !== 'function' || !c.subtle || typeof c.subtle.encrypt !== 'function') {
    throw new EncryptionUnavailableError();
  }
}

interface KeyRecord {
  id: string;
  key: CryptoKey;
  createdAt: number;
}

class KeyDb extends Dexie {
  keys!: Table<KeyRecord, string>;
  constructor(name: string) {
    super(name);
    this.version(1).stores({ keys: 'id' });
  }
}

const KEY_ID = 'data-key';

/** The key database that belongs to a data database. */
export function keyDbName(dataDbName: string): string {
  return `${dataDbName}-key`;
}

async function withKeyDb<T>(dataDbName: string, fn: (db: KeyDb) => Promise<T>): Promise<T> {
  const db = new KeyDb(keyDbName(dataDbName));
  try {
    await db.open();
    return await fn(db);
  } finally {
    db.close();
  }
}

/** The device key, if one exists. */
export function loadDeviceKey(dataDbName: string): Promise<CryptoKey | undefined> {
  return withKeyDb(dataDbName, async (db) => (await db.keys.get(KEY_ID))?.key);
}

/**
 * The device key, created on first use. Creation runs inside an IndexedDB
 * transaction so two tabs starting at once end up with the same key.
 */
export async function loadOrCreateDeviceKey(dataDbName: string): Promise<CryptoKey> {
  assertWebCrypto();
  return withKeyDb(dataDbName, (db) =>
    db.transaction('rw', db.keys, async () => {
      const existing = await db.keys.get(KEY_ID);
      if (existing) return existing.key;
      const key = await Dexie.waitFor(
        crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']),
      );
      await db.keys.put({ id: KEY_ID, key, createdAt: Date.now() });
      return key;
    }),
  );
}

/** Destroy the device key (crypto-shredding). */
export async function deleteDeviceKey(dataDbName: string): Promise<void> {
  await Dexie.delete(keyDbName(dataDbName));
}
